import React from 'react';
import classNames from 'classnames';

import AngleUpSvg from '../../../design-system/icons/core/angle-up.svg?component';
import AngleDownSvg from '../../../design-system/icons/core/angle-down.svg?component';
import { RCIcon } from '../RCIcon';

interface IProps {
  /** Renders to the right of the heading text */
  additionalHeadingContent?: React.ReactNode
  /** The content to be displayed */
  children: React.ReactNode;
  /** The text that displays as the title */
  heading: string;
  /** Used to auto-scroll/jump to a specific section and for aria controls */
  id: string;
  /** The content is always displayed */
  lockOpen?: boolean;
  /** The content starts open but can be hidden */
  defaultOpen?: boolean;
  /** Text is rendered at 100% instead of the default of 90% */
  noResize?: boolean;
  /** A function to call when the component is toggled */
  onToggle?: (state: boolean) => void;
  /** Reduces the size of the component */
  small?: boolean;
}

export function RCToggledContent({
  additionalHeadingContent,
  children,
  heading,
  id,
  lockOpen,
  defaultOpen,
  noResize,
  onToggle,
  small,
}: IProps) {
  const [collapsed, setCollapsed] = React.useState(!(lockOpen || defaultOpen));

  function toggle() {
    if (lockOpen) return;
    if (onToggle) {
      onToggle(!collapsed);
    }

    setCollapsed(value => !value);
  }

  const classes = classNames(
    'rc-toggled-content',
    { 'rc-toggled-content--collapsed': collapsed,
      'rc-toggled-content--locked': lockOpen,
      'rc-toggled-content--no-resize': noResize,
      'rc-toggled-content--small': small,
    },
  );

  return (
    <div
      className={classes}
      id={`rc-toggled-content__${id}`}
    >
      <h3
        className="rc-toggled-content__heading"
      >
        <button
          type="button"
          onClick={toggle}
          disabled={lockOpen}
          className="rc-toggled-content__heading__button"
          aria-expanded={!collapsed}
          aria-controls={`rc-toggled-content__${id}-content`}
        >
          <span className="rc-toggled-content__heading__title">
            {heading}
          </span>
          {additionalHeadingContent}
          {!lockOpen && (
            <span className="rc-toggled-content__header__icon">
              <RCIcon svgComponent={collapsed ? AngleDownSvg : AngleUpSvg} />
            </span>
          )}
        </button>
      </h3>
      <div className="rc-toggled-content__inner" hidden={collapsed} id={`rc-toggled-content__${id}-content`}>
        {children}
      </div>
    </div>
  );
}
